import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import {
  DashboardIcon,
  CustomersIcon,
  TasksIcon,
  CalendarIcon,
  DealsBoardIcon,
  ConversationsIcon,
  PhoneIcon,
  CalculatorIcon,
  UsersIcon,
  GripVerticalIcon,
  UserIcon,
  BadgeIcon,
  ReportsIcon
} from './Icons'

export interface NavigationItem {
  id: string
  name: string
  href: string
  icon: React.ComponentType<any>
  translationKey: string
}

interface SidebarEditorProps {
  isOpen: boolean
  onClose: () => void
  currentItems: NavigationItem[]
  availableItems: NavigationItem[]
  onSave: (items: NavigationItem[]) => void
  onReset: () => void
}

export const SidebarEditor: React.FC<SidebarEditorProps> = ({
  isOpen,
  onClose,
  currentItems,
  availableItems,
  onSave,
  onReset
}) => {
  const { t } = useTranslation()
  const [selectedItems, setSelectedItems] = useState<NavigationItem[]>(currentItems)

  useEffect(() => {
    setSelectedItems(currentItems)
  }, [currentItems])

  if (!isOpen) return null

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const items = Array.from(selectedItems)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    setSelectedItems(items)
  }

  const handleRemove = (itemId: string) => {
    setSelectedItems(selectedItems.filter(item => item.id !== itemId))
  }

  const handleAdd = (item: NavigationItem) => {
    if (!selectedItems.find(i => i.id === item.id)) {
      setSelectedItems([...selectedItems, item])
    }
  }

  const handleSave = () => {
    onSave(selectedItems)
    onClose()
  }

  const handleDiscard = () => {
    setSelectedItems(currentItems)
    onClose()
  }

  const handleReset = () => {
    onReset()
    onClose()
  }

  const getAvailableItemsToAdd = () => {
    return availableItems.filter(
      availItem => !selectedItems.find(selItem => selItem.id === availItem.id)
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('sidebar.customizeNavigation')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('sidebar.dragToReorder')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current Items */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                {t('sidebar.currentPages')}
              </h3>
              {selectedItems.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                  No pages selected. Add pages from the available list.
                </p>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="current-pages">
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={`space-y-2 ${snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-900/10 rounded-lg p-2' : ''}`}
                      >
                        {selectedItems.map((item, index) => {
                          const Icon = item.icon
                          return (
                            <Draggable key={item.id} draggableId={item.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-all ${
                                    snapshot.isDragging 
                                      ? 'shadow-lg border-2 border-gray-900' 
                                      : 'border border-gray-200 dark:border-gray-600'
                                  }`}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div
                                      {...provided.dragHandleProps}
                                      className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                      <GripVerticalIcon size={20} />
                                    </div>
                                    <Icon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {t(item.translationKey)}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleRemove(item.id)}
                                    className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                                    title={t('sidebar.clickToRemove')}
                                  >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </Draggable>
                          )
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>

            {/* Available Items */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                {t('sidebar.availablePages')}
              </h3>
              <div className="space-y-2">
                {getAvailableItemsToAdd().length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                    All available pages are already in your sidebar.
                  </p>
                ) : (
                  getAvailableItemsToAdd().map((item) => {
                    const Icon = item.icon
                    return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <Icon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {t(item.translationKey)}
                          </span>
                        </div>
                        <button
                          onClick={() => handleAdd(item)}
                          className="text-gray-700 hover:text-gray-900 dark:text-gray-600 dark:hover:text-blue-300"
                          title={t('sidebar.clickToAdd')}
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              {t('sidebar.resetToDefault')}
            </button>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleDiscard}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {t('sidebar.discardChanges')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg hover:from-gray-900 hover:to-purple-700 transition-all"
              >
                {t('sidebar.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
